import type { ReactNode } from 'react';
import SearchIcon from '../../assets/icons/search.svg?react';
import { FooterCell, Table } from '../UI';
import type { TableColumn, TableSortState } from '../UI';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import styles from './RoomListView.module.css';

interface RoomListViewProps<T> {
  title: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  columns: TableColumn<T>[];
  rooms: T[];
  getRowKey: (room: T) => string;
  sort: TableSortState | null;
  onSortChange: (key: string) => void;
  // The add row is the only write control this view owns; the columns bring
  // their own. Omitted entirely for a user who may not write.
  canWrite: boolean;
  addLabel: string;
  isAddingRoom: boolean;
  onStartAdd: () => void;
  onSaveNewRoom: (name: string) => void;
  onCancelAdd: () => void;
  // Delete confirmation and anything else the list needs on top.
  children?: ReactNode;
}

/**
 * The shared frame of the room and intervention-room settings lists: heading,
 * search box, sortable table and the footer add row. What differs between the
 * two lists is its columns and its labels.
 */
export const RoomListView = <T,>({
  title,
  searchLabel,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  columns,
  rooms,
  getRowKey,
  sort,
  onSortChange,
  canWrite,
  addLabel,
  isAddingRoom,
  onStartAdd,
  onSaveNewRoom,
  onCancelAdd,
  children
}: RoomListViewProps<T>) => {
  return (
    <section>
      <SectionHeader title={title}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{searchLabel}</label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={searchPlaceholder}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={rooms}
        sort={sort}
        onSortChange={onSortChange}
        getRowKey={getRowKey}
        footer={
          canWrite ? (
            <FooterCell
              isEditing={isAddingRoom}
              addLabel={addLabel}
              onStartEdit={onStartAdd}
              onSave={onSaveNewRoom}
              onCancel={onCancelAdd}
            />
          ) : undefined
        }
      />

      {children}
    </section>
  );
};
